import React from 'react';
import { PagerProps } from '@progress/kendo-react-data-tools';
import { Button } from '@progress/kendo-react-buttons';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import CaretToLeftIcon from '../../assets/icons/caretToLeftIcon.svg?react';
import CaretLeftIcon from '../../assets/icons/caretLeftIcon.svg?react';
import CaretRightIcon from '../../assets/icons/caretRightIcon.svg?react';
import CaretToRightIcon from '../../assets/icons/caretToRightIcon.svg?react';
import { SaveViewChangesButton } from '../toolbar/SaveViewChangesButton';
import { chevronDownIcon } from '@progress/kendo-svg-icons';
import {
  useGlobalizationContext,
  useTranslation,
  useSearchContext,
  useViewsContext,
  useCurrentUserContext,
  useGetViews
} from '@cs-cms/state-management';
import { GRID_PAGE_SIZES } from '@cs-cms/types';
import { CpsSvgIcon } from '../../shared/CpsSvgIcon';
import { exceedsMaxSearchSkip } from '../helpers/cpsGridSkipLimit';
import { GridPagerSkipLimitDialog } from './GridPagerSkipLimitDialog';

interface ExtendedPagerProps extends PagerProps {
  columnChanges?: {
    widths: Record<string, number>;
    order: string[];
  };
  columns?: any[];
  onSaveComplete?: () => void;
}

export const GridPager = (props: ExtendedPagerProps) => {
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const element = React.useRef<HTMLDivElement>(null);
  const { compositeFilterDescriptor, setCompositeFilterDescriptor } = useSearchContext();
  const { selectedView } = useViewsContext();
  const { selectedIndex } = useCurrentUserContext();
  const { data: views } = useGetViews(selectedIndex?.id);
  const [isSkipLimitDialogVisible, setIsSkipLimitDialogVisible] = React.useState(false);

  const clearFilters = () => {
    if (setCompositeFilterDescriptor) {
      setCompositeFilterDescriptor({
        logic: 'and',
        filters: []
      });
    }
  };

  const currentPage = Math.floor(props.skip / props.take) + 1;
  const totalPages = Math.ceil((props.total ?? 0) / props.take);

  const handlePageChange = (newPage: number): void => {
    let validatedPageNumber = newPage;
    if (newPage < 1) {
      validatedPageNumber = 1;
    } else if (newPage > totalPages) {
      validatedPageNumber = totalPages;
    }

    const skip = (validatedPageNumber - 1) * props.take;
    if (exceedsMaxSearchSkip(skip)) {
      setIsSkipLimitDialogVisible(true);
      return;
    }

    props.onPageChange?.({
      target: { element: element.current, props },
      skip,
      take: props.take,
      syntheticEvent: {} as any,
      nativeEvent: {} as any,
      targetEvent: { value: validatedPageNumber }
    });
  };

  const handlePageSizeChange = (newSize: number) => {
    props.onPageChange?.({
      target: { element: element.current, props },
      skip: 0,
      take: newSize,
      syntheticEvent: {} as any,
      nativeEvent: {} as any,
      targetEvent: { value: newSize }
    });
  };

  const hasFilters = (compositeFilterDescriptor?.filters || []).length > 0;
  const isCurrentViewPrivate = views?.find(x => x.id === selectedView)?.isPrivate;

  return (
    <div ref={element} className="k-pager k-pager-sm k-grid-pager customGridPager">
      <span>
        {props.total === 0
          ? `0 - 0 ${translate('grid.pagerOf')} 0`
          : `${props.skip + 1} - ${Math.min(props.skip + props.take, props.total)} ${translate('grid.pagerOf')} ${props.total}`}
      </span>

      <div className="k-pager-numbers-wrap pagerNavButtons">
        <Button
          disabled={currentPage === 1 || props.total === 0}
          onClick={() => handlePageChange(1)}
          title="Go to the first page"
          fillMode="flat"
          className="pagerArrowButton pagerToButton"
        >
          <CaretToLeftIcon />
        </Button>

        <Button
          disabled={currentPage === 1 || props.total === 0}
          onClick={() => handlePageChange(currentPage - 1)}
          title="Go to the previous page"
          fillMode="flat"
          className="pagerArrowButton"
        >
          <CaretLeftIcon />
        </Button>

        <Button
          disabled={currentPage === totalPages || props.total === 0}
          onClick={() => handlePageChange(currentPage + 1)}
          title="Go to the next page"
          fillMode="flat"
          className="pagerArrowButton"
        >
          <CaretRightIcon />
        </Button>

        <Button
          disabled={currentPage === totalPages || props.total === 0}
          onClick={() => handlePageChange(totalPages)}
          title="Go to the last page"
          fillMode="flat"
          className="pagerArrowButton pagerToButton"
        >
          <CaretToRightIcon />
        </Button>

        <div className="customGridPagerSizes">
          <DropDownList
            data={GRID_PAGE_SIZES}
            value={props.take}
            onChange={e => {
              handlePageSizeChange(e.value);
            }}
            size="small"
            svgIcon={chevronDownIcon}
          />
          <span>{translate('grid.pagerItemsPerPage')}</span>
        </div>
      </div>

      <div className="pagerClearFilterContainer">
        {props.columnChanges && isCurrentViewPrivate && (
          <SaveViewChangesButton
            columnChanges={props.columnChanges}
            columns={props.columns}
            onSaveComplete={props.onSaveComplete}
          />
        )}

        {hasFilters && (
          <Button
            fillMode="solid"
            themeColor={'primary'}
            size={'small'}
            onClick={clearFilters}
            title={translate('custom.toolbar.filterGridClear')}
            className="pagerButton"
          >
            {translate('custom.toolbar.filterGridClear')}
            <CpsSvgIcon customIcon="clearFilterIcon" className={`cpsIcon cpsIconMedium`} onClick={clearFilters} />
          </Button>
        )}
      </div>

      {isSkipLimitDialogVisible && (
        <GridPagerSkipLimitDialog translate={translate} onConfirm={() => setIsSkipLimitDialogVisible(false)} />
      )}
    </div>
  );
};
